import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function useCamera(open, facing) {
  const videoRef = useRef(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) { return undefined; }
    let stream;
    let cancelled = false;
    setError("");
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => setError("Fotocamera non disponibile: consenti l'accesso alla fotocamera nel browser oppure usa la galleria."));
    if (!navigator.mediaDevices) setError("Questo browser non consente l'uso della fotocamera: usa la galleria.");
    return () => { cancelled = true; stream?.getTracks().forEach((t) => t.stop()); };
  }, [open, facing]);
  return { videoRef, error };
}

export function CameraDialog({ open, onOpenChange, title, onCapture }) {
  const [facing, setFacing] = useState("environment");
  const [busy, setBusy] = useState(false);
  const { videoRef, error } = useCamera(open, facing);
  const shoot = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setBusy(true);
    c.toBlob(async (blob) => {
      try { await onCapture(blob); onOpenChange(false); } finally { setBusy(false); }
    }, "image/jpeg", 0.9);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[95dvh] overflow-y-auto" data-testid="camera-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Inquadra il documento dentro il riquadro, ben illuminato e senza riflessi.</DialogDescription></DialogHeader>
        {error ? <p className="text-sm text-red-600" data-testid="camera-error">{error}</p> : (
          <div className="relative rounded-xl overflow-hidden bg-black">
            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-[60dvh] object-contain" data-testid="camera-video" />
            <div className="pointer-events-none absolute inset-[10%] rounded-xl border-2 border-dashed border-white/70" />
          </div>
        )}
        <div className="flex flex-wrap gap-2 justify-end">
          <button type="button" onClick={() => setFacing(facing === "environment" ? "user" : "environment")} className="btn-secondary h-11 px-4" data-testid="camera-switch-button"><RefreshCw className="h-4 w-4" /> Cambia fotocamera</button>
          <button type="button" onClick={shoot} disabled={!!error || busy} className="btn-primary h-11 px-5" data-testid="camera-shoot-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />} Scatta
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
